import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UrlService} from '../../services/url.service';
import {AuthenticationService} from '../../services/authentication.service';
import {ActivatedRoute} from '@angular/router';
import {DirectoryService} from 'app/services/directory.service';
import {SearchQueryField} from '../../types/models';
import {SearchSelectItem} from '../../types/search-select';
import {Subscription} from 'rxjs/Subscription';

@Component({
    selector: 'filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss']
})
export class FilterComponent implements OnInit, OnDestroy {
    @Input() tabHeading: string;

    @Input() set buildings(buildings) {
        if (buildings) {
            this.convertedBuildings = this.convertToSearchSelectFormat(buildings);
        }
    }

    @Input() placeholder = 'All Buildings';
    @Input() urlParameterName = 'building';
    searchFields: SearchQueryField[] = [];
    searchDelay: any;
    searchString = '';
    selectedBuilding: string;
    quickSearch: string;
    optionsFilter = 'Broad Match';
    convertedBuildings = [];
    private subscriptions: Subscription[] = [];

    constructor(
        private urlService: UrlService,
        public authenticationService: AuthenticationService,
        private route: ActivatedRoute,
        public directoryService: DirectoryService,
    ) {
    }

    ngOnInit() {
        const queryParams$ = this.route.queryParams.subscribe(qp => {
            this.urlService.queryParams.query = qp.query ? qp.query : '';
            this.urlService.queryParams.tab = qp.tab ? qp.tab : null;
            this.updateParams();
            this.updateFieldCheckboxes();
            if (qp.query) {
                this.directoryService.quickSearchStore$.next(null);
            }
            if (this.urlService.queryParams.query && this.urlService.queryParams.query.indexOf('*') === -1) {
                this.optionsFilter = 'Exact Match';
            } else {
                this.optionsFilter = 'Broad Match';
            }
        });
        this.subscriptions.push(queryParams$);

        const instantOnUrlChange$ = this.urlService.instantOnUrlChange.subscribe(() => {
            //Here the query string is parsed to get the actual search input value separated from other elements
            if (this.urlService.queryParams.query) {
                if (this.urlService.queryParams.query[0] == ':') {
                    const split = this.urlService.queryParams.query.split(':');
                    this.searchString = this.urlService.queryParams.query.slice(this.urlService.queryParams.query.indexOf(split[1]) + split[1].length + 1).replace(/^\*|\*$/g, '');
                } else
                    this.searchString = this.urlService.queryParams.query.replace(/^\*|\*$/g, '');
            } else
                this.searchString = '';
        });

        this.subscriptions.push(instantOnUrlChange$);

        this.selectedBuilding = this.urlService.queryParams[this.urlParameterName] || '';
        // this.optionsFilter = this.authenticationService.userIsResident ? 'Broad Match' : 'Exact Match';
        // this.optionsFilter = 'Broad Match';

        const quickSearchStore$ = this.directoryService.quickSearchStore$.subscribe(store => {
            if (this.urlService.queryParams.tab === undefined) {
                this.urlService.queryParams.tab = 'residents';
            }
            if (store && this.urlService.queryParams.tab === 'residents') {
                this.quickSearch = store.residentName;
                this.searchString = this.quickSearch;
                if (!this.directoryService.selectedItemData || this.directoryService.selectedItemData !== store.resident) {
                    this.directoryService.selectedItemData = store.resident;
                    this.searchOnInput();
                }
            }
        });

       this.subscriptions.push(quickSearchStore$);

        if (this.directoryService.quickSearchStore) {
            this.directoryService.quickSearchStore$.next(this.directoryService.quickSearchStore);
        }

       this.updateFieldCheckboxes();
    }

    /**
     * Updates properties (searchFields and searchString)
     * with current URL state
     */
    updateParams() {
        const tab = this.urlService.queryParams.tab;
        this.searchFields = this.urlService.queryFields.hasOwnProperty(tab) ? this.urlService.queryFields[tab] : [];
        if (this.urlService.queryParams.query) {
            this.urlService.queryParams.query = this.urlService.queryParams.query.split(':')[this.urlService.queryParams.query.split(':').length - 1];
            const queryData = /\*(.*)\*$/g.exec(this.urlService.queryParams.query);
            if (queryData) {
                this.searchString = '';
                const searchTerm = queryData[queryData.length - 1];
                this.searchString = searchTerm.replace(/^\*|\*$/g, ''); // remove * from beginning and end of the string
            }
        }
    }

    search(): void {
        let searchTerm: string;

        if (this.optionsFilter === 'Broad Match') {
            searchTerm = this.searchString ? `*${this.searchString}*` : '';
        } else {
            searchTerm = this.searchString ? `${this.searchString}` : '';
        }

        if (this.authenticationService.userIsResident) {
            this.urlService.updateSearchQuery(searchTerm, null, true);
        } else {
            this.urlService.updateSearchQuery(searchTerm, null, false);
        }
        this.urlService.setQueryParams(null, 1);
    }

    searchOnInput(): void {
        clearTimeout(this.searchDelay);
        this.searchDelay = setTimeout(() => {
            if (this.route.snapshot['_routerState'].url.indexOf('tab=residents') != -1 && this.searchString.indexOf('@') != -1)
                this.searchFields.find(f => f.name == 'Email').isSelected = true;
            this.search();
        }, 500);
    }

    clearSearch(): void {
        this.urlService.queryParams.query = this.searchString = null;
        this.directoryService.showDetailedView = false;
        this.directoryService.selectedItemData = null;
        this.directoryService.quickSearchStore$.next(null);
        this.directoryService.quickSearchStore = null;
        this.urlService.setQueryParams(null, 1);
    }

    filterByBuilding(buildingId: string) {
        this.selectedBuilding = buildingId;
        this.urlService.queryParams[this.urlParameterName] = buildingId;
        this.urlService.setQueryParams(null, 1);
    }

    convertToOptionsFilter() {
        return [{value: 'Exact Match', optionText: 'Exact Match'}, {value: 'Broad Match', optionText: 'Broad Match'}];
    }

    convertToSearchSelectFormat(buildings: any[]): Array<SearchSelectItem> {
        const converted: Array<SearchSelectItem> = buildings.map(b => ({
            value: b.id,
            optionText: b.name,
            name: b.name,
            address: b.address,
            corporationNumberName: b.corporationNumberName,
            searchProp: `${b.name} ${b.address} ${b.corporationNumberName}`
        }));

        if (buildings.length > 0 && this.placeholder) {
            converted.unshift({value: '', optionText: this.placeholder});
        }

        return converted;
    }

    optionsFilterData(option: string) {
        this.optionsFilter = option;
        if (this.searchString) this.searchOnInput();
    }

    /**
     * Updates field checkboxes
     * with URL search parameter
     */
    updateFieldCheckboxes() {
        if (!this.urlService.queryParams.query) {
            return;
        }

        const queryData = this.urlService.queryParams.query.split(':');
        const searchFields = queryData[1] || '';

        if (searchFields) {
            const searchFieldsArr = searchFields.split('|');
            this.searchFields.forEach(field => field.isSelected = searchFieldsArr.includes(field.field));
        }
    }

    onSearchFieldChanged() {
        if (this.searchString) {
            this.search();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}



// WEBPACK FOOTER //
// /app/src/app/components/filter/filter.component.ts